/**
 * Kremser–Colburn tenglamalari: suyultirilgan absorbsiya, chiziqli muvozanat y* = m·x.
 *
 * MANBA: Kasatkin, gl. XVI (absorbsiya: число единиц переноса, минимальный расход);
 *        Pavlov, Romankov, Noskov, gl. 6 (misollar 6.1–6.4);
 *        Treybal, Mass-Transfer Operations, Ch. 8 (eq. 8.48 Colburn, 8.50 Kremser);
 *        Perry's 8th ed., Sec. 14 "Absorption" (eq. 14-22..14-26).
 *
 * TENGLAMALAR (A = L/(m·G), y* = m·xIn):
 *   Colburn (N_OG, A ≠ 1):
 *     N_OG = ln[ (1 − 1/A)·(yIn − y*)/(yOut − y*) + 1/A ] / (1 − 1/A)
 *   A = 1:  N_OG = (yIn − yOut)/(yOut − y*)
 *   Teskari:  yOut = y* + (yIn − y*)·(1 − 1/A) / (exp(N_OG·(1 − 1/A)) − 1/A)
 *   Kremser (nazariy bosqichlar):
 *     N = ln[ ((yIn − y*)/(yOut − y*))·(1 − 1/A) + 1/A ] / ln A;   A = 1: N = (yIn − yOut)/(yOut − y*)
 *   Minimal erituvchi:  (L/G)min = (yIn − yOut)/(yIn/m − xIn)
 *   Balans:             xOut = xIn + G·(yIn − yOut)/L
 *
 * ASSUMPTION:
 *   1. Suyultirilgan eritma: G, L o'zgarmas; mol ulushlari ≈ mol nisbatlari.
 *   2. Izotermik (yutilish issiqligi e'tiborga olinmagan) → m o'zgarmas.
 *   3. Muvozanat chizig'i to'g'ri chiziq va (0,0) dan o'tadi (y* = m·x).
 *   4. N_OG gaz fazasi umumiy qarshiligi bo'yicha (K_y·a); suyuqlik fazasi qarshiligi K_y·a ichida.
 */

export function absorptionFactor(L: number, G: number, m: number): number {
  return L / (m * G);
}

/** Colburn: N_OG. Maqsad muvozanat bilan erishib bo'lmas bo'lsa Infinity */
export function ntuOverallGas(
  yIn: number,
  yOut: number,
  xIn: number,
  m: number,
  A: number,
): number {
  const yStar = m * xIn;
  const dIn = yIn - yStar;
  const dOut = yOut - yStar;
  if (!(dOut > 0) || !(dIn > 0)) return dOut >= dIn ? 0 : Infinity;
  if (dOut >= dIn) return 0;
  if (Math.abs(A - 1) < 1e-9) return (yIn - yOut) / dOut;
  const s = 1 - 1 / A;
  const arg = (s * dIn) / dOut + 1 / A;
  if (!(arg > 0)) return Infinity;
  return Math.log(arg) / s;
}

/** Teskari Colburn: berilgan N_OG uchun yOut */
export function outletFromNtu(
  yIn: number,
  xIn: number,
  m: number,
  A: number,
  N_OG: number,
): number {
  const yStar = m * xIn;
  const dIn = yIn - yStar;
  if (!(dIn > 0)) return yIn; // gaz allaqachon muvozanatda yoki past — yutilish yo'q
  if (!Number.isFinite(N_OG)) return A > 1 ? yStar : yStar + dIn * (1 - A);
  if (Math.abs(A - 1) < 1e-9) return yStar + dIn / (1 + N_OG);
  const s = 1 - 1 / A;
  return yStar + (dIn * s) / (Math.exp(N_OG * s) - 1 / A);
}

/** Kremser: ekvivalent nazariy bosqichlar */
export function kremserStages(
  yIn: number,
  yOut: number,
  xIn: number,
  m: number,
  A: number,
): number {
  const yStar = m * xIn;
  const dIn = yIn - yStar;
  const dOut = yOut - yStar;
  if (!(dOut > 0) || !(dIn > 0)) return dOut >= dIn ? 0 : Infinity;
  if (dOut >= dIn) return 0;
  if (Math.abs(A - 1) < 1e-9) return (yIn - yOut) / dOut;
  const arg = (dIn / dOut) * (1 - 1 / A) + 1 / A;
  if (!(arg > 0)) return Infinity;
  return Math.log(arg) / Math.log(A);
}

/** (L/G)min — erituvchi chiqishda kiruvchi gaz bilan muvozanatda; erishib bo'lmasa Infinity */
export function minLiquidToGas(yIn: number, yOut: number, xIn: number, m: number): number {
  const denom = yIn / m - xIn;
  if (!(denom > 0)) return Infinity;
  const r = (yIn - yOut) / denom;
  return r < 0 ? 0 : r;
}
